/**
 * @fileoverview PrincipalAvatar — circular avatar button rendered as the
 * trailing item in the Carbon workspace-bar actions slot. Reuses the base
 * `.bp-principal-avatar` class shared with the rail-side initials avatar so
 * the existing 22px rail rendering stays byte-identical; this standalone
 * button form adds `.bp-principal-avatar--md` (28px + cursor pointer).
 *
 * `variant="you"` adds a `.bp-principal-avatar--you` ring/outline modifier
 * to mark the current user's own avatar — Foundry uses this inside the
 * UserAccountMenu header to signal "this is *you*".
 *
 * Foundry mapping: `carbon-app__user-menu` +
 * `multipass-principal-components__principal-avatar` (+ `__you-avatar`
 * modifier for the `variant="you"` form).
 *
 * Content priority: imageUrl → initials (first 2 uppercased) → generic
 * Blueprint <Icon icon="user" />.
 */

import { Icon } from "@blueprintjs/core";
import type { ReactElement } from "react";

interface PrincipalAvatarProps {
  initials?: string;
  imageUrl?: string;
  onClick?: () => void;
  ariaLabel?: string;
  /** "you" applies the current-user ring outline modifier. */
  variant?: "default" | "you";
  className?: string;
}

export function PrincipalAvatar({
  initials,
  imageUrl,
  onClick,
  ariaLabel = "Open user menu",
  variant = "default",
  className,
}: PrincipalAvatarProps): ReactElement {
  return (
    <button data-testid="principal-avatar"
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className={[
        "bp-principal-avatar",
        "bp-principal-avatar--md",
        variant === "you" && "bp-principal-avatar--you",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {imageUrl ? (
        <img src={imageUrl} alt="" />
      ) : initials ? (
        <span>{initials.slice(0, 2).toUpperCase()}</span>
      ) : (
        <Icon icon="user" />
      )}
    </button>
  );
}
